import React, { createContext, useContext, useState } from 'react';
import localPersistenceHelper from 'helpers/localPersistenceHelper.js';
import constants from 'constants/tooltips.js';

const STORAGE_KEY = 'tooltips';

const defaultValue = {
  [constants.AI_GROUNDING]: {
    lastUpdated: null,
    step: 1,
    count: 2,
  },
  [constants.AI_NAVIGATION]: {
    lastUpdated: null,
    count: 2,
  },
  [constants.THEMING_UI]: {
    lastUpdated: null,
    count: 2,
  },
  [constants.CREATE_SHOW]: {
    lastUpdated: null,
    count: 2,
    step: 1,
  },
};

export const TooltipContext = createContext(defaultValue);

export const useTooltipContext = () => useContext(TooltipContext);


export const TooltipContextProvider = ({ children }) => {
  const [state, setState] = useState({
    ...defaultValue,
    ...localPersistenceHelper.getItem({ key: STORAGE_KEY }),
  });

  const updateTooltipContext = (tooltipKey) => {
    if (!state[tooltipKey]) return;
    const { setOrUpdateObjectItem } = localPersistenceHelper;
    setOrUpdateObjectItem({
      key: STORAGE_KEY,
      data: {
        ...state,
        [tooltipKey]: {
          count: state[tooltipKey]?.count - 1,
          lastUpdated: Date.now(),
        },
      },
    });
    setState((prevState) => ({
      ...prevState,
      [tooltipKey]: {
        ...state[tooltipKey],
        count: state[tooltipKey]?.count - 1,
        lastUpdated: Date.now(),
      },
    }));
  };

  const updateStepTooltipContext = (tooltipKey, action) => {
    let newStep = 0;
    switch (action) {
      case 'previous':
        newStep = state[tooltipKey]?.step > 0 ? state[tooltipKey]?.step - 1 : 0;
        break;
      case 'next':
        newStep = state[tooltipKey]?.step + 1;
        break;
      default:
        break;
    }
    setState((prevState) => ({
      ...prevState,
      [tooltipKey]: {
        ...state[tooltipKey],
        step: newStep,
      },
    }));
  };

  const isTooltipVisible = (tooltipKey) => {
    const { count = 2, lastUpdated = Date.now() } = state[tooltipKey];
    const ONE_DAY = 24 * 60 * 60 * 1000;
    const canShowAs24HoursPassed = Date.now() - lastUpdated > ONE_DAY;
    const canShowAsCountIsGreaterThanZero = count > 0;
    return canShowAs24HoursPassed && canShowAsCountIsGreaterThanZero;
  };

  const isStepTooltipVisible = (tooltipKey, step) => {
    const isGeneralTooltipVisible = isTooltipVisible(tooltipKey);
    const isStepVisible = state[tooltipKey]?.step === step;
    return isGeneralTooltipVisible && isStepVisible;
  };

  const context = {
    ...state,
    updateTooltipContext,
    updateStepTooltipContext,
    isStepTooltipVisible,
    isTooltipVisible,
  };

  return <TooltipContext.Provider value={context}>{children}</TooltipContext.Provider>;
};
